/* eslint react/prop-types: 0 */
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import BoardIdentity, { IDENTITY } from '../../helpers/traceability/boardIdentity';
import { profileForBoard } from '../../helpers/traceability/boardModel';
import { buildFirmwareSketch } from '../../helpers/traceability/firmwareSketch';
import { firmwareUploadFor } from '../../helpers/traceability/firmwareUploads';
import { MODULE_EVENT, misplacedModule, moduleEvents } from '../../helpers/traceability/modulePorts';
import TraceabilityLink from '../../helpers/traceability/traceabilityLink';
import BoardIdentityModal from './BoardIdentityModal';
import InstallFirmwareModal from './InstallFirmwareModal';
import MisplacedModuleModal from './MisplacedModuleModal';
import showModuleToast from './ModuleToast';

/**
 * Los estados en los que hay algo puesto en pantalla. Se mira desde afuera para
 * avisar que este modal toma el lugar del de vinculación.
 */
const showsModal = (state) => state === IDENTITY.FIRMWARE_MISSING
  || state === IDENTITY.INSTALLING
  || state === IDENTITY.NEEDS_USB
  || state === IDENTITY.READING_USB
  || state === IDENTITY.PROVISIONING;

/**
 * Trazabilidad de las placas vinculadas.
 *
 * Por cada placa abre el puerto, averigua con qué número se identifica y se lo
 * pasa al agente, que es lo que suelta el evento de conexión que estaba
 * esperando. Después le pregunta cada tanto qué módulos ve y lo escribe en la
 * consola del navegador.
 *
 * También avisa lo que se enchufa y se desenchufa en la placa seleccionada: un
 * toast por cada cambio y un modal cuando un módulo está en un puerto que no lo
 * acepta.
 *
 * Sigue a todas las placas y no solo a la seleccionada, porque el evento de
 * conexión de cada una espera su identidad: una placa que nadie mira se quedaría
 * sin reportar hasta que venza el plazo del agente. Lo que sí es solo de la
 * seleccionada son los modales -- instalar el firmware, leer los datos --, porque
 * los dos necesitan a alguien adelante, y grabar el firmware usa el flujo de
 * compilación del panel, que es uno solo. Una placa no seleccionada que necesita
 * al usuario se queda esperando, y cuando la seleccionen sigue desde ahí.
 *
 * Vive afuera de BlocklyPanel a propósito: el panel solo lo monta, le dice qué
 * puertos hay y le presta su flujo de compilación para el botón Instalar.
 */
class BoardTraceability extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Nombre del puerto -> lo último que contó su máquina de identidad.
      snapshots: {},
      /**
       * Los puertos en los que ya se intentó instalar el firmware. Es lo que
       * distingue el primer pedido de un reintento, que es lo único que cambia en
       * el modal cuando la instalación falla.
       */
      attempted: {},
      // Nombre del puerto -> los módulos mal puestos que falta avisar, en orden.
      misplaced: {},
    };
    /**
     * Nombre del puerto -> lo último que se avisó de sus módulos ({ serial,
     * modules }). Vive fuera de cada link a propósito: el link se vacía al
     * instalar el firmware y se arma de nuevo cuando la placa se vuelve a
     * enumerar, y sin esto cada carga de programa repetiría todos los toasts. Se
     * olvida cuando en el puerto aparece otra placa.
     */
    this.knownModules = new Map();
    // Nombre del puerto -> lo que se armó para seguirlo.
    this.entries = new Map();
    this.mounted = false;
    // Lo que espera a que termine la grabación del firmware. El panel avisa
    // bajando `installing`, y la máquina de identidad espera una promesa.
    this.pendingInstall = null;
    this.handleInstall = this.handleInstall.bind(this);
    this.handleRead = this.handleRead.bind(this);
    this.handleMisplacedClose = this.handleMisplacedClose.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    this.syncPorts();
  }

  componentDidUpdate(previousProps, previousState) {
    const { installing } = this.props;

    /**
     * Se revisa en cada render y no solo cuando cambia la lista: el nombre del
     * puerto llega antes de que el agente tenga el SerialPort, y el agente mismo
     * puede cambiar de tipo en el medio. syncPorts corta solo para los que ya
     * están enganchados al mismo puerto, así que revisar de más no cuesta nada.
     */
    this.syncPorts();

    // La grabación terminó: lo que la estaba esperando puede seguir.
    if (previousProps.installing && !installing && this.pendingInstall) {
      const { resolve } = this.pendingInstall;
      this.pendingInstall = null;
      resolve();
    }

    this.announceModal(previousState);
  }

  /**
   * Avisa cuando uno de estos modales aparece, para que el de vinculación se
   * cierre solo.
   *
   * El de vinculación termina en una pantalla con un botón "Entendido" que el
   * usuario cierra cuando quiere, y mientras tanto la placa ya se está revisando:
   * si tarda en cerrarlo, el de firmware le aparece encima. Cerrarlo desde acá es
   * lo único que no depende de cuánto tarde la persona.
   *
   * Solo en la transición, no en cada render: el usuario podría haber abierto el
   * de vinculación a propósito después, y cerrarlo de nuevo sería pelearle.
   */
  announceModal(previousState) {
    const { port, onModalOpen } = this.props;
    if (!onModalOpen) return;
    const { snapshots } = this.state;
    const before = showsModal(previousState.snapshots[port]?.state);
    const now = showsModal(snapshots[port]?.state);
    if (now && !before) onModalOpen();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.entries.forEach((entry, name) => this.dropPort(name));
  }

  /**
   * Da de alta a las placas nuevas y de baja a las que se fueron.
   *
   * Un puerto cuyo objeto SerialPort cambió cuenta como otro: eso es lo que pasa
   * cuando una placa con usb nativo se reinicia y vuelve a enumerarse, y el link
   * viejo quedó hablándole a un puerto que ya no existe.
   */
  syncPorts() {
    const { socket, ports } = this.props;
    const supported = !!socket?.supportsTraceability?.();
    const listed = supported ? (ports || []).filter(Boolean) : [];

    const seen = new Set();
    listed.forEach((port) => {
      const name = port.Name;
      const serialPort = socket.getSerialPort(name);
      if (!serialPort) return;
      seen.add(name);
      const entry = this.entries.get(name);
      if (entry && entry.serialPort === serialPort) return;
      if (entry) this.dropPort(name);
      this.trackPort(name, serialPort);
    });

    this.entries.forEach((entry, name) => {
      if (!seen.has(name)) this.dropPort(name);
    });
  }

  trackPort(name, serialPort) {
    const { socket, board, user } = this.props;

    // La placa decide con qué tabla se nombran los módulos que lea.
    const link = new TraceabilityLink(serialPort, {
      portName: name,
      board,
      // Si le cargamos código, se la busca primero a la velocidad que declara.
      preferredBaudRates: () => [firmwareUploadFor(name)?.baudRate].filter(Boolean),
    });
    const identity = new BoardIdentity(link, {
      portName: name,
      // Lo que el servidor de seriales necesita para elegir un número.
      context: {
        compilerBoard: board?.compilerBoard || null,
        profile: profileForBoard(board),
        user: user ? { userId: user.userId, email: user.email } : null,
      },
      installFirmware: () => this.runInstall(),
      expectsFirmware: () => !!firmwareUploadFor(name),
      readUsbSerial: () => socket.readUsbSerialFor(serialPort, { prompt: true }),
      readGrantedUsbSerial: () => socket.readUsbSerialFor(serialPort, { prompt: false }),
    });

    const unsubscribe = identity.subscribe((snapshot) => {
      // La máquina sigue andando mientras el componente se desmonta, y avisarle a
      // uno que ya no está es un warning de React y nada más.
      if (!this.mounted) return;
      this.setState((previous) => ({ snapshots: { ...previous.snapshots, [name]: snapshot } }));
      /**
       * La identidad se le pasa al agente apenas está: es lo que suelta el evento
       * de conexión de esta placa, que se estaba reteniendo justamente por esto.
       * Sin número también se avisa -- ver resolve en BoardIdentity --, porque lo
       * que corresponde ahí es reportarla igual.
       */
      if (snapshot.state === IDENTITY.READY) socket.setBoardIdentity(serialPort, snapshot.identity);
    });

    const unsubscribeModules = link.subscribe((snapshot) => {
      if (this.mounted) this.handleModules(name, snapshot);
    });

    this.entries.set(name, {
      serialPort, link, identity, unsubscribe, unsubscribeModules,
    });
    identity.start();
  }

  dropPort(name) {
    const entry = this.entries.get(name);
    if (!entry) return;
    this.entries.delete(name);
    entry.unsubscribe();
    entry.unsubscribeModules();
    entry.identity.dispose();
    entry.link.dispose();
    if (this.mounted) {
      this.setState((previous) => {
        const snapshots = { ...previous.snapshots };
        const attempted = { ...previous.attempted };
        const misplaced = { ...previous.misplaced };
        delete snapshots[name];
        delete attempted[name];
        delete misplaced[name];
        return { snapshots, attempted, misplaced };
      });
    }
  }

  /**
   * Compara los módulos del snapshot del link con lo último que se avisó en ese
   * puerto y avisa lo que cambió.
   *
   * Los toasts son solo de la placa seleccionada, que es la que el usuario está
   * mirando; de las demás se toma nota igual, para que al seleccionarlas no
   * aparezca de golpe todo lo que pasó antes. Los modales pendientes sí se
   * guardan para todas: un módulo mal puesto sigue mal puesto cuando se cambia de
   * placa.
   */
  handleModules(name, snapshot) {
    const { board, port, intl } = this.props;
    const { serialNumber, modules } = snapshot;
    let known = this.knownModules.get(name);
    if (!known || (serialNumber && known.serial && known.serial !== serialNumber)) {
      known = { serial: null, modules: {} };
    }
    const { known: next, events } = moduleEvents(board, known.modules, modules);
    this.knownModules.set(name, { serial: serialNumber || known.serial, modules: next });
    if (events.length === 0) return;

    if (name === port) events.forEach((event) => showModuleToast(intl, event));

    this.setState((previous) => {
      let queue = previous.misplaced[name] || [];
      events.forEach((event) => {
        // Lo que pasó en un puerto reemplaza lo que estaba pendiente de él.
        queue = queue.filter((item) => item.port !== event.port);
        const wrong = event.type === MODULE_EVENT.CONNECTED && misplacedModule(board, event.reading);
        if (wrong) queue = [...queue, wrong];
      });
      return { misplaced: { ...previous.misplaced, [name]: queue } };
    });
  }

  handleMisplacedClose() {
    const { port } = this.props;
    this.setState((previous) => ({
      misplaced: { ...previous.misplaced, [port]: (previous.misplaced[port] || []).slice(1) },
    }));
  }

  /**
   * Le pasa el sketch al panel, que lo compila y lo graba por el camino de
   * siempre: mismo perfil de placa, mismo modal de progreso. La promesa se cierra
   * cuando el panel baja `installing`.
   */
  runInstall() {
    const { onInstall } = this.props;
    return new Promise((resolve) => {
      this.pendingInstall = { resolve };
      onInstall(buildFirmwareSketch());
    });
  }

  handleInstall() {
    const { port } = this.props;
    const entry = this.entries.get(port);
    if (!entry) return;
    this.setState((previous) => ({ attempted: { ...previous.attempted, [port]: true } }));
    entry.identity.install();
  }

  handleRead() {
    const { port } = this.props;
    const entry = this.entries.get(port);
    if (entry) entry.identity.readUsb();
  }

  render() {
    const { snapshots, attempted, misplaced } = this.state;
    const { installing, port, board } = this.props;
    // Los modales son de la placa seleccionada: son los dos pasos que necesitan a
    // alguien adelante, y adelante hay una sola persona.
    const state = snapshots[port]?.state;

    return (
      <>
        <InstallFirmwareModal
          open={state === IDENTITY.FIRMWARE_MISSING || state === IDENTITY.INSTALLING}
          installing={state === IDENTITY.INSTALLING || !!installing}
          failed={!!attempted[port] && state === IDENTITY.FIRMWARE_MISSING}
          onInstall={this.handleInstall}
        />
        <BoardIdentityModal state={state} onRead={this.handleRead} />
        {/* Espera a que terminen los pasos de identificación: no se apilan modales. */}
        <MisplacedModuleModal
          board={board}
          misplaced={showsModal(state) ? null : (misplaced[port] || [])[0]}
          onClose={this.handleMisplacedClose}
        />
      </>
    );
  }
}

export default injectIntl(BoardTraceability);
